import React, { 
    useEffect, 
    useState, 
    useRef,
    forwardRef,
    useImperativeHandle
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select'
import Tagify from "@yaireo/tagify";

import { specialCharacterValidate } from '../../../../../../../../../component/common/Helper';
import AlertMessage from '../../../../../../../../../component/common/AlertMessage';
import ValidationMessage from '../../../../../../../../../component/common/ValidationMessage';
import ButtonLoader from '../../../../../../../../../component/common/ButtonLoader';
import { followUpDetailsOptions } from '../../../../../../../../../constants/selectOptions';

const ServiceForm = forwardRef((props, ref) => {
    const {
        formValidateError, 
        serviceFormSave,
        updateServiceList,
        formActionType,
        formData,
        isButtonLoader
    } = props;

    // ----- set tagify ----- //
    const serviceSynonyms = useRef(null);
    const tagifyRef = useRef(null);
    useEffect(() => {
        if (serviceSynonyms.current) {
            // tagifyRef.current = new Tagify(serviceSynonyms.current);
            tagifyRef.current = new Tagify(serviceSynonyms.current, {
                duplicates: true,
                enforceWhitelist: false,
                callbacks: {
                    add: (e) => {
                        const newTag = e.detail.data.value;
                        const occurrences = tagifyRef.current.value.filter(
                            tag => tag.value === newTag
                        ).length;
                        if (occurrences > 1) {
                            tagifyRef.current.removeTag(e.detail.tag);
                        }
                    }
                }
            });
        }
        return () => {
            if (tagifyRef.current) {
                tagifyRef.current.destroy();
            }
        };
    },[]);

    const [ serviceFormError, setServiceFormError ] = useState({});
    const [ serviceFormData, setServiceFormData ] = useState({ 
        service_name : '',
        service_synonyms : '',
        action_name : '',
        api_end_point : '',
        service_follow_up_details: null,
        service_affiram_or_deny : null,
        service_closing_utterance : "",
        service_feedback : "",
        select_action : null,
    });

    const validateField = (name, value) => {
        let error = "";
        if (name === "service_name") {
            if (!value) {
                error = "Service name is required.";
            }
        }
        if (name === "action_name") {
            if (!value) {
                error = "Action name is required.";
            }
        }
        return error;
    }

    const [serviceFollowUpDetails, setServiceFollowUpDetails] = useState('');
    const [serviceAffiramOrDeny, setServiceAffiramOrDeny] = useState('');
    const serviceInputOnChange = (selectedOption, actionMeta) => {
        if (actionMeta?.name) {
            // ------- manage select field --------- //
            const { name } = actionMeta;
            if(name === 'service_follow_up_details'){
                if(selectedOption){
                    setServiceFollowUpDetails(selectedOption.value)
                }
                else{
                    setServiceFollowUpDetails('')
                }
            }

            if(name === 'service_affiram_or_deny'){
                if(selectedOption){
                    setServiceAffiramOrDeny(selectedOption.value)
                }
                else{
                    setServiceAffiramOrDeny('')
                }
            }

            setServiceFormData((prevData) => ({
                ...prevData,
                [name]: selectedOption,
            }));
            const fieldError = validateField(name, selectedOption);
            setServiceFormError((prevErrors) => ({
                ...prevErrors,
                [name]: fieldError,
            }));
        }
        else if(selectedOption?.target){
            const { name, value } = selectedOption.target;

            let newVal = value
            const fieldsToValidate = [
                'service_name', 
                'action_name',
            ];
            if (fieldsToValidate.includes(name)) {
                newVal = specialCharacterValidate(newVal);
            }

            // ----- manage input field -------- //
            setServiceFormData((prevData) => ({
                ...prevData,
                [name]: newVal,
            }));
            const fieldError = validateField(name, value);
            setServiceFormError((prevErrors) => ({
                ...prevErrors,
                [name]: fieldError,
            }));
        }
    }

    const serviceSaveOnClick = (e) => {
        e.preventDefault();
        const newErrors = {};
        for (const key in serviceFormData) {
            newErrors[key] = validateField(key, serviceFormData[key]);
        }
        setServiceFormError(newErrors);
        if (Object.keys(newErrors).every((key) => !newErrors[key])) {
            serviceFormSave(serviceFormData)
        }
    }

    useImperativeHandle(ref, () => ({
        resetForm: () => {
            setServiceFormData({
                service_name : '',
                service_synonyms : '',
                action_name : '',
                api_end_point : '',
                service_follow_up_details: null,
                service_affiram_or_deny : null,
                service_closing_utterance : "",
                service_feedback : "",
                select_action : null,
            });
            setServiceFollowUpDetails('')
        }
    }));

    useEffect(() => {
        if( updateServiceList.length > 0 ){
            if(formActionType === 'update'){
                let serviceUpdateData = updateServiceList[0];
                setServiceFormData((prevState) => ({
                    ...prevState,
                    service_name: serviceUpdateData.service_name,
                    action_name: serviceUpdateData.action_name,
                    api_end_point: serviceUpdateData.api_end_point ? serviceUpdateData.api_end_point : '',
                }));

                setTimeout(() => {
                    if (serviceSynonyms.current) {
                        tagifyRef.current.removeAllTags();
                        tagifyRef.current.addTags(serviceUpdateData.service_synonyms);
                    }
                }, 100);

                if(serviceUpdateData.service_follow_up_details){
                    serviceInputOnChange(
                        serviceUpdateData.service_follow_up_details, 
                        {action : "select-option",name : "service_follow_up_details"}
                    )
                    if(serviceUpdateData.service_follow_up_details.value === 'closing_utterance'){
                        setServiceFormData((prevState) => ({
                            ...prevState,
                            service_closing_utterance: serviceUpdateData.service_closing_utterance,
                        }));

                    }
                    if(serviceUpdateData.service_follow_up_details.value === 'feedback_utterance'){
                        setServiceFormData((prevState) => ({
                            ...prevState,
                            service_feedback: serviceUpdateData.service_feedback,
                        }));
                    }
                }
                if(serviceUpdateData.select_action){
                    serviceInputOnChange(
                        serviceUpdateData.select_action, 
                        {action : "select-option",name : "select_action"}
                    )
                }

                if(serviceUpdateData.service_affiram_or_deny){
                    serviceInputOnChange(
                        serviceUpdateData.service_affiram_or_deny, 
                        {action : "select-option",name : "service_affiram_or_deny"}
                    )
                }
            }
            else{
                setServiceFormData((prevState) => ({
                    ...prevState,
                    service_name : '',
                    service_synonyms : '',
                    action_name : '',
                    api_end_point : '',
                    service_follow_up_details: null,
                    service_affiram_or_deny : null,
                    service_closing_utterance : "",
                    service_feedback : "",
                    select_action : null,
                }));
            }
        }
    },[updateServiceList]);

    // ------ action data manage --------- //
    const [ actionName, setActionName ] = useState([]);
    const othersFormSetupAction = formData.step2.others_form_setup;
    const pfAssAction = formData.step3.product_features_association_data;
    const serviceAction = formData.step4.service_setup;
    const serviceCombinedData = [
        ...othersFormSetupAction, 
        ...pfAssAction, 
        ...serviceAction
    ];
    useEffect(() => {
        if(serviceCombinedData.length > 0){
            const uniqueActions = [...new Map(serviceCombinedData.map(item => [item.action_name, item])).values()];
            var selectAction = uniqueActions.map(item => ({
                label: item.action_name,
                value: item.action_name
            }));
            setActionName(selectAction)
        }
    }, [othersFormSetupAction, pfAssAction, serviceAction])

    const infoPopupData = {
        'service_name' : {
            'title' : 'Service name',
            'info' : `The Service name should be unique. For example, Book a Cab, Address Change Request and so on.`,
        },
        'service_synonyms' : {
            'title' : 'Synonyms',
            'info' : `Enlist all synonyms that go with your Product or Service name.`,
        },
        'action_name' : {
            'title' : 'Action name',
            'info' : `Give a unique name for this specific Action. All the information will be saved and used under this Action Name.`,
        },
        'service_follow_up_details' : {
            'title' : 'Follow up details',
            'info' : `
                Follow-up Actions automatically trigger an Agent response after fulfilling the customer query.
                For example, after providing product information, you may want the Agent to ask, "Would you
                like to place an order now?" Opt-in Action is when you would like the Agent to query like,
                "Would you like to place an order?". If the customer says Yes, you can add Sell Action as the
                Follow-up Action. Note that Sell Action should be pre-created to be invoked here. If you have
                not already created, you can come back here using Edit Agent mode and add it later. If the
                customer denies with a No, you can provide some response such as, "That is fine. Let me know
                if I can do anything else for you." <br/><br/>
                Use Closing Utterance for a simple closing response such as, "Glad to be of help." Feedback is a
                specific type of Follow-up Action where you can collect Ratings or some feedback. This feature
                is not available yet. Use this for providing information in an interactive mode to your
                customers. It can be a Product or a Service that you provide. For example, your customer may
                inquire about the EV you sell or a Hotel Accommodation. <br/><br/>
                Note that you can also provide an API end point for response for each Service Action, in the
                next step. You may use this to fetch a response dynamically from a 3rd party system or even an
                LLM outside. However, using API end point is optional. If you do not want to do that, you can
                use the Closing Utterance above to give a conclusive message at the end of the Service
                delivery. <br/><br/>
                For example, you are collection a payment for a sale. After you collected all details required for
                the payment: a) You can use an API end-point to dynamically ping the payment gateway,
                generate order number and give back to your customer. Here you have to provide the full
                Utterance/Response such as “Your order has been successfully placed. Your order number is
                XYZ.” b) You can provide a standard response using the Closing Utterance above such as “Thank
                you for placing the order. We will inform you shortly about it.”
            `,
        },
        'api_end_point': {
            'title' : 'API end point',
            'info' : `You can also provide an API end point for response for each Service Action here. You may use
            this to fetch a response dynamically from a 3rd party system or even an LLM outside. However,
            using API end point is optional. If you do not want to do that, you can use the Closing Utterance
            above to give a conclusive message at the end of the Service delivery. <br/> <br/>
            For example, you are collection a payment for a sale. After you collected all details required for
            the payment: a) You can use an API end-point to dynamically ping the payment gateway,
            generate order number and give back to your customer. Here you have to provide the full
            Utterance/Response such as “Your order has been successfully placed. Your order number is
            XYZ.” b) You can provide a standard response using the Closing Utterance above such as “Thank
            you for placing the order. We will inform you shortly about it.”`
        }
    }
    const infoOpenOnCLick = (param) => {
        props.isInfoOpenHandler({
            type: 'open',
            data: infoPopupData[param]
        })
    }
    
    return (
        <>
            <div className='row'>
                <div className='col-md-12 mb-5'>
                    <label className="form-label required">
                        Service Name
                    </label>
                    <span 
                        className="fs-6 fw-semibold align-self-start cursor-pointer float-end"
                        onClick={(e) => infoOpenOnCLick('service_name')}
                    >
                        <FontAwesomeIcon className='fs-5 text-gray-600' icon={faCircleInfo} />
                    </span>
                    <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter service name"
                        name="service_name"
                        onChange={serviceInputOnChange}
                        value={serviceFormData.service_name}
                        autoFocus
                    />
                    {
                        serviceFormError.service_name &&
                        <ValidationMessage className='text-danger' message ={serviceFormError.service_name}/>
                    }
                </div>
                <div className='col-md-12 mb-5'>
                    <label className="form-label">
                        Synonyms
                    </label>
                    <span 
                        className="fs-6 fw-semibold align-self-start cursor-pointer float-end ms-5"
                        onClick={(e) => infoOpenOnCLick('service_synonyms')}
                    >
                        <FontAwesomeIcon className='fs-5 text-gray-600' icon={faCircleInfo} />
                    </span>
                    <span 
                        className="fs-6 fw-semibold text-primary ms-2 float-end">
                        (Press Enter to add each synonym)
                    </span>
                    <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter service synonyms"
                        ref={serviceSynonyms}
                        name="service_synonyms"
                        onChange={serviceInputOnChange}
                        value={serviceFormData.service_synonyms}
                    />
                </div>
                <div className='col-md-12 mb-5'>
                    <label className="form-label required">
                        Action Name
                    </label>
                     <span 
                        className="fs-6 fw-semibold align-self-start cursor-pointer float-end"
                        onClick={(e) => infoOpenOnCLick('action_name')}
                    >
                        <FontAwesomeIcon className='fs-5 text-gray-600' icon={faCircleInfo} />
                    </span>
                    
                    <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter action name"
                        name="action_name"
                        onChange={serviceInputOnChange}
                        value={serviceFormData.action_name}
                    />
                    {
                        serviceFormError.action_name &&
                        <ValidationMessage className='text-danger' message ={serviceFormError.action_name}/>
                    }
                </div>
                <div className='col-md-12 mb-5'>
                    <label className="form-label">
                        API end-point
                    </label>
                     <span 
                        className="fs-6 fw-semibold align-self-start cursor-pointer float-end"
                        onClick={(e) => infoOpenOnCLick('api_end_point')}
                    >
                        <FontAwesomeIcon className='fs-5 text-gray-600' icon={faCircleInfo} />
                    </span>
                    <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter API end point"
                        name="api_end_point"
                        onChange={serviceInputOnChange}
                        value={serviceFormData.api_end_point}
                    />
                </div>
                <div className='col-md-12 mb-5'>
                    <label className="form-label">
                        Follow up details
                    </label>
                    <span 
                        className="fs-6 fw-semibold align-self-start cursor-pointer float-end"
                        onClick={(e) => infoOpenOnCLick('service_follow_up_details')}
                    >
                        <FontAwesomeIcon className='fs-5 text-gray-600' icon={faCircleInfo} />
                    </span>
                    <Select
                        className='react-select-styled react-select-solid react-select-sm' 
                        classNamePrefix='react-select' 
                        options={followUpDetailsOptions}
                        placeholder='Select an option' 
                        name='service_follow_up_details'
                        onChange={serviceInputOnChange}
                        value={serviceFormData.service_follow_up_details}
                        isClearable
                    />
                </div>
                {/* {
                    serviceFollowUpDetails === 'opt_in' &&
                    <div className='col-md-12 mb-5'>
                        <label className="form-label">
                            Affiram or Deny
                        </label>
                        <Select
                            className='react-select-styled react-select-solid react-select-sm' 
                            classNamePrefix='react-select' 
                            options={[{label: 'Yes', value: 'yes'},{label: 'No', value:'no'} ]}
                            placeholder='Select an option' 
                            name='service_affiram_or_deny'
                            onChange={serviceInputOnChange}
                            value={serviceFormData.service_affiram_or_deny}
                            isClearable
                        />
                    </div>
                } */}
                {
                    serviceFollowUpDetails === 'closing_utterance' &&
                    <div className='col-md-12 mb-5'>
                        <label className="form-label">
                            Closing Utterance
                        </label>
                        <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder="Enter Closing Utterance"
                            name="service_closing_utterance"
                            onChange={serviceInputOnChange}
                            value={serviceFormData.service_closing_utterance}
                        />
                    </div>
                }
                {
                    serviceFollowUpDetails === 'feedback_utterance' &&
                    <div className='col-md-12 mb-5'>
                        <label className="form-label">
                            Feedback Utterance
                        </label>
                        <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder="Enter feedback"
                            name="service_feedback"
                            onChange={serviceInputOnChange}
                            value={serviceFormData.service_feedback}
                        />
                    </div>
                }
                {
                    serviceFollowUpDetails === 'opt_in' &&
                    <div className='col-md-12'>
                        <label className="form-label">
                            Select Action
                        </label>
                        <Select
                            className='react-select-styled react-select-solid react-select-sm' 
                            classNamePrefix='react-select' 
                            options={actionName}
                            placeholder='Select an option' 
                            name='select_action'
                            onChange={serviceInputOnChange}
                            value={serviceFormData.select_action}
                            isClearable
                        />
                    </div>
                }
                <div className='col-md-12 m-auto text-end mt-5'>
                    {
                        formActionType === 'insert' &&
                        <div 
                            className={`btn btn-dark btn-icon btn-sm ${isButtonLoader ? 'disabled' : ''}`}
                            onClick={serviceSaveOnClick}
                        >
                            {
                                isButtonLoader ? 
                                <ButtonLoader/>
                                :
                                <FontAwesomeIcon icon={faFloppyDisk} className='fs-5' />
                            }
                        </div>
                    }
                    {
                        formActionType === 'update' &&
                        <div 
                            className={`btn btn-primary btn-sm ${isButtonLoader ? 'disabled' : ''}`} 
                            onClick={serviceSaveOnClick}
                        >
                            {
                                isButtonLoader ? 
                                <ButtonLoader
                                    btnHtml='Please wait...'
                                    className='me-2'
                                />
                                :
                                'Update'
                            }
                        </div>
                    }
                </div>
                <div className='col-md-12 mt-5'>
                    {
                        formValidateError?.service_setup &&
                        <AlertMessage
                            sub_title = {formValidateError?.service_setup}
                            type = 'danger'
                        />
                    }
                </div>
            </div>
        </>
    )
})

export default ServiceForm